import { createContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import useAuth from '../hooks/useAuth';
import LoadingScreen from '../components/LoadingScreen';
import { db } from '../lib/firebase';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/es';
import pkg from '../../package.json';
import { appUpdate } from '../lib/appUpdateSignal';
import { setCountriesConfig } from '../utils/countryConfig';
import ContextError from '../pages/ContextError';
import MaintenanceCurtain from '../components/MaintenanceCurtain';

dayjs.extend(utc);

const ParametersContext = createContext({ parametrosDB: null, usersDB: null, timeDatesDB: null });

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 800;

// Returns true if `a` is strictly greater than `b` by semver. Non-numeric segments compare as 0.
function isVersionGreater(a, b) {
  if (!a || !b) return false;
  const aParts = a.split('.').map((n) => Number(n) || 0);
  const bParts = b.split('.').map((n) => Number(n) || 0);
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i += 1) {
    const av = aParts[i] || 0;
    const bv = bParts[i] || 0;
    if (av > bv) return true;
    if (av < bv) return false;
  }
  return false;
}

export function ParametersProvider(props) {
  const { children } = props;
  const version = useRef(pkg.version);
  const [isLoading, setIsLoading] = useState(0);
  const [parametrosDB, setParametrosDB] = useState(null);
  const [usersDB, setUsersDB] = useState([]);
  const [timeDatesDB, setTimeDatesDB] = useState('');
  const [feriadosDB, setFeriadosDB] = useState([]);
  const [feriadosByCountry, setFeriadosByCountry] = useState({});
  const [changelog, setChangelog] = useState(null);
  const [maintenanceDB, setMaintenanceDB] = useState(null);
  const [hasError, setHasError] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const unsubFromParametersRef = useRef();
  const unsubFromUsersRef = useRef();
  const unsubFromTimeDatesRef = useRef();
  const unsubFromFeriadosRef = useRef();
  const unsubFromMaintenanceRef = useRef();
  const retryTimersRef = useRef([]);
  // console.log(user);

  // Subscribes to a Firestore listener with retry + error fallback. Exceptions thrown
  // inside a snapshot callback (e.g. reading a field off undefined data) and Firestore
  // listener-level errors both leave `isLoading` stuck otherwise. On failure the listener
  // is retried up to MAX_RETRIES more times; if it keeps failing, `hasError` renders the
  // ContextError page.
  // - subscribe(onNext, onError) -> unsubscribe fn : wires the actual Firestore query
  // - handle(snapshot)                             : reads data + sets state (may throw)
  // - unsubRef                                     : ref holding the active unsubscribe fn
  const subscribeWithRetry = (subscribe, handle, unsubRef, attempt = 0) => {
    setIsLoading((prevValue) => prevValue + 1);
    let settled = false;

    const fail = (error) => {
      console.error('Error setting parameters context: ', error);
      if (unsubRef.current) {
        unsubRef.current();
      }
      if (!settled) {
        settled = true;
        setIsLoading((prevValue) => prevValue - 1);
      }
      if (attempt < MAX_RETRIES) {
        const timerId = setTimeout(
          () => subscribeWithRetry(subscribe, handle, unsubRef, attempt + 1),
          RETRY_DELAY_MS
        );
        retryTimersRef.current.push(timerId);
      } else {
        setHasError(true);
      }
    };

    const onNext = (snapshot) => {
      try {
        handle(snapshot);
        if (!settled) {
          settled = true;
          setIsLoading((prevValue) => prevValue - 1);
        }
      } catch (error) {
        fail(error);
      }
    };

    unsubRef.current = subscribe(onNext, fail);
  };

  const restoreParametros = () => {
    subscribeWithRetry(
      (onNext, onError) => db.collection('params').doc('global').onSnapshot(onNext, onError),
      (docSnapshot) => {
        const initialParams = docSnapshot.data();
        // Hydrate the per-country config cache (WS1) so countryConfig selectors
        // can resolve currency/VAT/locale without threading the map everywhere.
        setCountriesConfig(initialParams.countries);
        const Sucursales = initialParams.sucursal;
        const Organizaciones = initialParams.organizacion;
        const userSucursales = Sucursales.filter((obj) => user.sucu.includes(obj.value)); // Del auth token saco las sucursales del usuario
        const userOrganizaciones = Organizaciones.filter((obj) =>
          userSucursales.map((obj2) => obj2.org).includes(obj.value)
        ); // De las mismas sucursales del auth token saco las organizaciones del usuario
        if (!userSucursales.find((obj) => obj.value === user.sucursal.value)) {
          // The auth-token sucursal snapshot can lack fields the full params list carries
          // (e.g. country / utc used by WS1/WS5). Enrich from the full list, keeping the token's
          // own fields; fall back to the bare token only if the branch isn't in params.
          const fullSucursal = Sucursales.find((obj) => obj.value === user.sucursal.value);
          userSucursales.push(fullSucursal ? { ...fullSucursal, ...user.sucursal } : user.sucursal);
        }
        if (!userOrganizaciones.find((obj) => obj.value === user.organizacion.value)) {
          // The auth-token org snapshot ({ group, text, value }) carries no country / VAT, so reading
          // those off organizacionUser made the WS1 fail-closed selectors throw. Enrich from the full
          // params org list (always has country / VAT), keeping the token's own fields; fall back to
          // the bare token only if the org isn't in params.
          const fullOrg = Organizaciones.find((obj) => obj.value === user.organizacion.value);
          userOrganizaciones.push(fullOrg ? { ...fullOrg, ...user.organizacion } : user.organizacion);
        }
        const parametrosTotal = initialParams;
        parametrosTotal.sucursalUser = userSucursales;
        parametrosTotal.organizacionUser = userOrganizaciones;
        if (isVersionGreater(initialParams.version, version.current)) {
          appUpdate.markPending();
          setParametrosDB((prev) => prev ?? parametrosTotal);
          return;
        }
        setParametrosDB(parametrosTotal);
      },
      unsubFromParametersRef
    );
  };

  const restoreUsers = () => {
    subscribeWithRetry(
      (onNext, onError) => db.collection('users').where('externo', '!=', true).onSnapshot(onNext, onError),
      (querySnapshot) => {
        const users = querySnapshot.docs.map((docSnapshot) => docSnapshot.data());
        setUsersDB(users);
      },
      unsubFromUsersRef
    );
  };

  const restoreTimeDates = () => {
    subscribeWithRetry(
      (onNext, onError) => db.collection('params').doc('timedates').onSnapshot(onNext, onError),
      (docSnapshot) => {
        const timeDates = docSnapshot.data();
        if (!timeDates) {
          throw new Error('timedates sin datos.');
        }
        setTimeDatesDB(timeDates);
      },
      unsubFromTimeDatesRef
    );
  };

  const restoreFeriados = () => {
    subscribeWithRetry(
      (onNext, onError) => db.collection('params').doc('feriados').onSnapshot(onNext, onError),
      (docSnapshot) => {
        const feriados = docSnapshot.data()?.dias;
        if (!Array.isArray(feriados) || feriados.length === 0) {
          throw new Error('feriados sin datos.');
        }
        // Vista por país (WS5): las entradas traen countryCode (las legacy sin él son CL);
        // los días suprimidos por un admin (enabled === false) no llegan a los consumidores.
        const byCountry = {};
        feriados.forEach((dia) => {
          if (dia?.enabled === false) return;
          const country = (dia?.countryCode || 'CL').toUpperCase();
          if (!byCountry[country]) byCountry[country] = [];
          byCountry[country].push(dia);
        });
        setFeriadosByCountry(byCountry);
        setFeriadosDB(byCountry.CL || []);
      },
      unsubFromFeriadosRef
    );
  };

  // Curtain de mantención (params/maintenance). A propósito NO usa
  // subscribeWithRetry: es fail-open — un doc ausente o un error de lectura
  // jamás debe gatillar ContextError ni bloquear la carga de la app.
  const restoreMaintenance = () => {
    unsubFromMaintenanceRef.current = db
      .collection('params')
      .doc('maintenance')
      .onSnapshot(
        (docSnapshot) => {
          setMaintenanceDB(docSnapshot.data() ?? null);
        },
        (error) => {
          console.error('Error subscribing to params/maintenance: ', error);
          setMaintenanceDB(null);
        }
      );
  };

  const restoreChangelog = () => {
    db.collection('params')
      .doc('changelog')
      .get()
      .then((snapshot) => {
        const data = snapshot.data();
        setChangelog(data?.changelog ?? []);
      })
      .catch((err) => {
        console.error('Error fetching changelog data:', err);
      });
  };

  const getData = async () => {
    restoreParametros();
    restoreUsers();
    restoreTimeDates();
    restoreFeriados();
    restoreMaintenance();
    restoreChangelog();
  };

  useEffect(() => {
    // console.log(isAuthenticated);
    if (isAuthenticated) {
      getData();
    }
    return () => {
      retryTimersRef.current.forEach(clearTimeout);
      retryTimersRef.current = [];
      if (unsubFromParametersRef.current) {
        unsubFromParametersRef.current();
      }
      if (unsubFromUsersRef.current) {
        unsubFromUsersRef.current();
      }
      if (unsubFromTimeDatesRef.current) {
        unsubFromTimeDatesRef.current();
      }
      if (unsubFromFeriadosRef.current) {
        unsubFromFeriadosRef.current();
      }
      if (unsubFromMaintenanceRef.current) {
        unsubFromMaintenanceRef.current();
      }
      setParametrosDB(null);
      setUsersDB([]);
      setTimeDatesDB([]);
      setFeriadosDB([]);
      setFeriadosByCountry({});
      setMaintenanceDB(null);
    };
  }, [isAuthenticated]);

  return (
    <ParametersContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{ parametrosDB, usersDB, timeDatesDB, feriadosDB, feriadosByCountry, changelog, maintenanceDB }}
    >
      {/* La cortina envuelve también a LoadingScreen/ContextError: con la API en
          503 la carga puede quedarse pegada y la mantención igual debe verse. */}
      <MaintenanceCurtain maintenance={maintenanceDB}>
        {hasError ? (
          <ContextError />
        ) : isLoading > 0 ? (
          <LoadingScreen />
        ) : (
          [children]
        )}
      </MaintenanceCurtain>
    </ParametersContext.Provider>
  );
}

ParametersProvider.propTypes = { children: PropTypes.node.isRequired };

export const ParametersConsumer = ParametersContext.Consumer;

export default ParametersContext;
